import { Box, Flex, Text } from '@chakra-ui/react'
import React, { ReactNode, useCallback, useState } from 'react'
import { ExtensionBadge } from '~/components/shared/ExtensionBadge'
import {
  ContactDetails,
  ContactNumber,
} from '~/pages/Contacts/viewModels/Contacts.viewmodel'
import { Size } from '~/types/ThemeTypes'
import { formatPhoneNumber } from '~/utils/formatter'

import { toNounCasing } from '@mangovoice/core'
import { ContactSourceType } from '@mangovoice/types'

import { CallContactButton } from './CallContactButton'
import { useContactCardStyles } from './componentStyles/ContactCard.style'

interface ContactCardProps {
  contact: ContactDetails
  onStartCall: (
    callee:
      | string
      | {
          contactNumber: ContactNumber
          contact: ContactDetails
        },
  ) => void
  isRecentDialed?: boolean
}

export const ContactCard = ({
  contact,
  onStartCall,
  isRecentDialed,
}: ContactCardProps) => {
  const styles = useContactCardStyles()
  const [isHovered, setIsHovered] = useState(false)

  const getNumberComponents = useCallback(() => {
    let label: ReactNode
    let button: JSX.Element | null = null
    if (contact.numbers === undefined) {
      // No numbers for contact, no label/no button
      label = ''
      button = <></>
      return { label, button }
    } else {
      const handleStartCall = useCallback(
        (number: ContactNumber) => {
          onStartCall({ contactNumber: number, contact })
        },
        [contact],
      )

      if (contact.source === ContactSourceType.Extensions) {
        // Extension, need ExtensionBadge/no label
        label = (
          <ExtensionBadge
            size={Size.lg}
            extension={contact.defaultNumber.number}
          />
        )
      } else if (contact.id === -1) {
        // Non-contact number from search
        label = (
          <Text sx={styles.nameText} noOfLines={1}>
            Call {formatPhoneNumber(contact.defaultNumber.number)}
          </Text>
        )
      } else {
        // Regular contact
        label = (
          <Text sx={styles.numberText} noOfLines={1}>
            {`${toNounCasing(
              contact.defaultNumber?.label ?? '',
            )}: ${formatPhoneNumber(contact.defaultNumber?.number)}`}
          </Text>
        )
      }
      return {
        label,
        button: (
          <CallContactButton
            defaultContactNumber={contact.defaultNumber}
            otherContactNumbers={contact.numbers}
            onStartCall={handleStartCall}
            isRecentDialed={isRecentDialed}
            isHidden={!isHovered}
          />
        ),
      }
    }
  }, [contact, isHovered])

  const { label: Label, button: Button } = getNumberComponents()
  const handleMouseOver = () => {
    setIsHovered(true)
  }

  const handleMouseLeave = () => {
    setIsHovered(false)
  }

  return (
    <Flex
      sx={styles.container}
      onMouseOver={handleMouseOver}
      onMouseLeave={handleMouseLeave}
    >
      <Box sx={styles.labelContainer}>
        <Text sx={styles.nameText} noOfLines={1}>
          {contact.name}
        </Text>
        {Label}
      </Box>
      {Button}
    </Flex>
  )
}
